// src/page/TestSubmissionAnalysis/useTestSubmissionAnalysis.ts
import { useEffect, useState, useCallback } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";

export interface PerTestSeries {
  id: string;
  name: string;
  courseName: string;
  standard: string;
  totalSubmissions: number;
  avgScore: number;
  avgCorrect: number;
  avgIncorrect: number;
  avgUnattempted: number;
  avgTotalMarks: number;
}

export interface RecentSubmission {
  id: string;
  userName: string;
  userMobile: string;
  testSeriesName: string;
  courseName: string;
  standard: string;
  score: number;
  totalMarks: number;
  correctCount: number;
  incorrectCount: number;
  unAttemptedCount: number;
  totalQuestions: number;
  submittedAt: string;
}

export interface AnalysisData {
  totalSubmissions: number;
  overallStats: {
    avgScore: number;
    avgCorrect: number;
    avgIncorrect: number;
    avgUnattempted: number;
    avgTotalMarks: number;
  };
  monthlyData: {
    labels: string[];
    submissionCounts: number[];
    avgScores: number[];
  };
  perTestSeries: PerTestSeries[];
  perStandard: { standard: string; count: number; avgScore: number }[];
  perCourse: { courseName: string; count: number; avgScore: number }[];
  donutData: { correct: number; incorrect: number; unattempted: number };
  recentSubmissions: RecentSubmission[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
  };
}

export interface FilterOptions {
  standards: string[];
  courses: string[];
  testSeries: { id: string; name: string }[];
}

export interface QuestionAnalysis {
  questionIndex: number;
  questionId: string;
  totalAttempts: number;
  correctCount: number;
  incorrectCount: number;
  unattemptedCount: number;
  correctOption: string;
  accuracy: number;
}

export interface SeriesDetailData {
  seriesInfo: {
    testSeriesName: string;
    courseName: string;
    standard: string;
    languageName: string;
    totalMarks: number;
    totalQuestions: number;
  } | null;
  overallStats: {
    totalSubmissions: number;
    avgScore: number;
    avgCorrect: number;
    avgIncorrect: number;
    avgUnattempted: number;
    highestScore: number;
    lowestScore: number;
  };
  questionAnalysis: QuestionAnalysis[];
  submissions: {
    id: string;
    userName: string;
    userMobile: string;
    score: number;
    totalMarks: number;
    correctCount: number;
    incorrectCount: number;
    unAttemptedCount: number;
    totalQuestions: number;
    submittedAt: string;
  }[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
  };
}

const useTestSubmissionAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalysisData | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    standards: [],
    courses: [],
    testSeries: [],
  });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    testSeriesId: "",
    standard: "",
    courseName: "",
    dateFrom: "",
    dateTo: "",
  });

  // Test series detail modal state
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [seriesDetailLoading, setSeriesDetailLoading] = useState(false);
  const [seriesDetail, setSeriesDetail] = useState<SeriesDetailData | null>(null);
  const [seriesDetailPage, setSeriesDetailPage] = useState(1);

  // Load filter options once
  useEffect(() => {
    ApiService({ method: "GET", endpoint: apiPaths.filterOptions })
      .then((res: FilterOptions) => setFilterOptions(res))
      .catch((err: any) => console.error("filterOptions error:", err));
  }, []);

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: 10 };
      if (filters.testSeriesId) params.testSeriesId = filters.testSeriesId;
      if (filters.standard) params.standard = filters.standard;
      if (filters.courseName) params.courseName = filters.courseName;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const res = await ApiService({
        method: "GET",
        endpoint: apiPaths.submissionAnalysis,
        params,
      });
      setData(res);
    } catch (err) {
      console.error("submissionAnalysis error:", err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const fetchSeriesDetail = useCallback(async (seriesId: string, pg: number = 1) => {
    setSeriesDetailLoading(true);
    try {
      const res = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.submissionTestSeriesDetail}/${seriesId}`,
        params: { page: pg, limit: 10 },
      });
      setSeriesDetail(res);
    } catch (err) {
      console.error("seriesDetail error:", err);
    } finally {
      setSeriesDetailLoading(false);
    }
  }, []);

  const openSeriesDetail = (seriesId: string) => {
    setSelectedSeriesId(seriesId);
    setSeriesDetailPage(1);
    fetchSeriesDetail(seriesId, 1);
  };

  const closeSeriesDetail = () => {
    setSelectedSeriesId(null);
    setSeriesDetail(null);
    setSeriesDetailPage(1);
  };

  const changeSeriesDetailPage = (pg: number) => {
    setSeriesDetailPage(pg);
    if (selectedSeriesId) fetchSeriesDetail(selectedSeriesId, pg);
  };

  const applyFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ testSeriesId: "", standard: "", courseName: "", dateFrom: "", dateTo: "" });
    setPage(1);
  };

  return {
    loading,
    data,
    filterOptions,
    page,
    setPage,
    filters,
    applyFilter,
    clearFilters,
    // Series detail
    selectedSeriesId,
    seriesDetailLoading,
    seriesDetail,
    seriesDetailPage,
    openSeriesDetail,
    closeSeriesDetail,
    changeSeriesDetailPage,
  };
};

export default useTestSubmissionAnalysis;
